import AutoPromo from '@/core/components/AutoPromo/AutoPromo';
import CarouselDonationCards from '@/core/components/CarouselDonationCards';
import { useCatalogueSearchContext } from '@/core/components/CatalogueSearch';
import { ContainerWithMaxWidth } from '@/core/components/ContainerWithMaxWidth';
import SchemaOrg from '@/core/components/SchemaOrg';
import Store from '@/core/lib/new-architecture/store';
import Routes from '@/core/lib/router/routes';
import { TFunction, useTranslationContext } from '@/core/lib/translation/translation.context';
import { PageWithLayoutsAndProviders } from '@/core/pages/page.types';
import { QuerySortEnum } from '@/core/types/donation';
import { CategoriesList, CategoriesListProps } from '@/next/components/Categories/CategoriesList';
import { HomeHero } from '@/next/components/Hero/HomeHero';
import { Testimonials, TestimonialsProps } from '@/next/components/Testimonials/Testimonials';
import { UniverProps, Univers } from '@/next/components/Univers/Univers';

const getCategories = (t: TFunction): CategoriesListProps['categories'] => [
  {
    name: t('categories-list.list.childcare', { ns: 'home' }),
    url: new Routes.CatalogueRoute({ cat: 'accessoires-de-puericulture' }),
    img: '/assets/home/categories/biberon.png',
  },
  {
    name: t('categories-list.list.furniture', { ns: 'home' }),
    url: new Routes.CatalogueRoute({ cat: 'meubles' }),
    img: '/assets/home/categories/fauteuil.png',
  },
  {
    name: t('categories-list.list.appliances', { ns: 'home' }),
    url: new Routes.CatalogueRoute({ cat: 'electromenager' }),
    img: '/assets/home/categories/frigo.png',
  },
  {
    name: t('categories-list.list.clothing', { ns: 'home' }),
    url: new Routes.CatalogueRoute({ cat: 'vetements-chaussures-et-accessoires' }),
    img: '/assets/home/categories/vetements.png',
  },
  {
    name: t('categories-list.list.games', { ns: 'home' }),
    url: new Routes.CatalogueRoute({ cat: 'loisirs-et-jeux' }),
    img: '/assets/home/categories/jouet.png',
  },
  {
    name: t('categories-list.list.multimedia', { ns: 'home' }),
    url: new Routes.CatalogueRoute({ cat: 'high-tech-et-electronique' }),
    img: '/assets/home/categories/mobile.png',
  },
];

type GetUnivers = (t: TFunction) => UniverProps;
const getChildcare: GetUnivers = t => ({
  style: { backgroundColor: '#EDEDFD' },
  overTitle: t('univers.childcare.over-title', { ns: 'home' }),
  title: t('univers.childcare.title', { ns: 'home' }),
  description: t('univers.childcare.description', { ns: 'home' }),
  landscapeImg: '/assets/home/univers/enfants/horizontal.jpg',
  portraitImg: '/assets/home/univers/enfants/vertical.jpg',
  button: { title: t('univers.childcare.button', { ns: 'home' }), url: 'https://google.com' },
  universTag: 'tout-pour-mes-enfants',
  items: [
    {
      label: t('univers.childcare.items.vetements', { ns: 'home' }),
      img: '/assets/home/univers/enfants/vetements.png',
      tag: 'vetements-enfants',
    },
    {
      label: t('univers.childcare.items.jouets', { ns: 'home' }),
      img: '/assets/home/univers/enfants/jouets.png',
      tag: 'jouets',
    },
    {
      label: t('univers.childcare.items.puericulture', { ns: 'home' }),
      img: '/assets/home/univers/enfants/puericulture.png',
      tag: 'puericulture',
    },
    {
      label: t('univers.childcare.items.mobilier', { ns: 'home' }),
      img: '/assets/home/univers/enfants/mobilier.png',
      tag: 'mobilier-enfant',
    },
    {
      label: t('univers.childcare.items.culture', { ns: 'home' }),
      img: '/assets/home/univers/enfants/livres.png',
      tag: 'culture',
    },
  ],
});
const getBricoJardin: GetUnivers = t => ({
  style: { backgroundColor: '#F3EAE2' },
  overTitle: t('univers.brico-jardin.over-title', { ns: 'home' }),
  title: t('univers.brico-jardin.title', { ns: 'home' }),
  description: t('univers.brico-jardin.description', { ns: 'home' }),
  landscapeImg: '/assets/home/univers/brico-jardin/horizontal.jpg',
  portraitImg: '/assets/home/univers/brico-jardin/vertical.jpg',
  button: { title: t('univers.brico-jardin.button', { ns: 'home' }), url: '' },
  universTag: 'brico-jardin',
  items: [
    {
      label: t('univers.brico-jardin.items.bricolage', { ns: 'home' }),
      img: '/assets/home/univers/brico-jardin/outillages.png',
      tag: 'bricolage',
    },
    {
      label: t('univers.brico-jardin.items.jardin', { ns: 'home' }),
      img: '/assets/home/univers/brico-jardin/mobilier.png',
      tag: 'profiter-du-jardin',
    },
    {
      label: t('univers.brico-jardin.items.jardinage', { ns: 'home' }),
      img: '/assets/home/univers/brico-jardin/jardinage.png',
      tag: 'jardinage',
    },
    {
      label: t('univers.brico-jardin.items.materiaux', { ns: 'home' }),
      img: '/assets/home/univers/brico-jardin/materiaux.png',
      tag: 'materiaux',
    },
  ],
});
const getJeMinstalle: GetUnivers = t => ({
  style: { backgroundColor: '#FFF6E6' },
  overTitle: t('univers.je-m-installe.over-title', { ns: 'home' }),
  title: t('univers.je-m-installe.title', { ns: 'home' }),
  description: t('univers.je-m-installe.description', { ns: 'home' }),
  landscapeImg: '/assets/home/univers/maison/horizontal.jpg',
  portraitImg: '/assets/home/univers/maison/vertical.jpg',
  button: { title: t('univers.je-m-installe.button', { ns: 'home' }), url: '' },
  universTag: 'je-m-installe',
  items: [
    {
      label: t('univers.je-m-installe.items.salon', { ns: 'home' }),
      img: '/assets/home/univers/maison/salle-sejour.png',
      tag: 'tout-pour-le-salon',
    },
    {
      label: t('univers.je-m-installe.items.chambre-bureau', { ns: 'home' }),
      img: '/assets/home/univers/maison/chambre-bureau.png',
      tag: 'chambre-et-bureau',
    },
    {
      label: t('univers.je-m-installe.items.cuisine', { ns: 'home' }),
      img: '/assets/home/univers/maison/cuisine.png',
      tag: 'tout-pour-la-cuisine',
    },
    {
      label: t('univers.je-m-installe.items.sdb', { ns: 'home' }),
      img: '/assets/home/univers/maison/sdb.png',
      tag: 'tout-pour-la-salle-de-bain',
    },
    {
      label: t('univers.je-m-installe.items.electroménager', { ns: 'home' }),
      img: '/assets/home/univers/maison/electromenager.png',
      tag: 'electromenager',
    },
    {
      label: t('univers.je-m-installe.items.deco', { ns: 'home' }),
      img: '/assets/home/univers/maison/deco.png',
      tag: 'deco',
    },
  ],
});
const getSport: GetUnivers = t => ({
  style: { backgroundColor: '#E9E9E9' },
  overTitle: t('univers.sports.over-title', { ns: 'home' }),
  title: t('univers.sports.title', { ns: 'home' }),
  description: t('univers.sports.description', { ns: 'home' }),
  landscapeImg: '/assets/home/univers/sports-loisirs/horizontal.jpg',
  portraitImg: '/assets/home/univers/sports-loisirs/vertical.jpg',
  button: { title: t('univers.sports.button', { ns: 'home' }), url: '' },
  universTag: 'sport-et-loisirs',
  items: [
    {
      label: t('univers.sports.items.sport', { ns: 'home' }),
      img: '/assets/home/univers/sports-loisirs/sports.png',
      tag: 'sport',
    },
    {
      label: t('univers.sports.items.multimedia', { ns: 'home' }),
      img: '/assets/home/univers/sports-loisirs/multimedia.png',
      tag: 'multimedia',
    },
    {
      label: t('univers.sports.items.kraft', { ns: 'home' }),
      img: '/assets/home/univers/sports-loisirs/loisirs-creatifs.png',
      tag: 'loisirs-creatifs',
    },
    {
      label: t('univers.sports.items.boardgames', { ns: 'home' }),
      img: '/assets/home/univers/sports-loisirs/jeux-societes.png',
      tag: 'jeux-de-societes',
    },
    {
      label: t('univers.sports.items.movies', { ns: 'home' }),
      img: '/assets/home/univers/sports-loisirs/films-musique.png',
      tag: 'films-et-musique',
    },
    {
      label: t('univers.sports.items.books', { ns: 'home' }),
      img: '/assets/home/univers/sports-loisirs/livres.png',
      tag: 'livres',
    },
  ],
});
const getFashionAndWellBeing: GetUnivers = t => ({
  style: { backgroundColor: '#FFEEEB' },
  overTitle: t('univers.mode-soins.over-title', { ns: 'home' }),
  title: t('univers.mode-soins.title', { ns: 'home' }),
  description: t('univers.mode-soins.description', { ns: 'home' }),
  landscapeImg: '/assets/home/univers/mode-soins/horizontal.jpg',
  portraitImg: '/assets/home/univers/mode-soins/vertical.jpg',
  button: { title: t('univers.mode-soins.button', { ns: 'home' }), url: '' },
  universTag: 'mode-et-bien-etre',
  items: [
    {
      label: t('univers.mode-soins.items.clothes', { ns: 'home' }),
      img: '/assets/home/univers/mode-soins/vetements.png',
      tag: 'vetements',
    },
    {
      label: t('univers.mode-soins.items.beauty-care', { ns: 'home' }),
      img: '/assets/home/univers/mode-soins/hygiene-beaute.png',
      tag: 'hygiene-beaute',
    },
    {
      label: t('univers.mode-soins.items.accessories', { ns: 'home' }),
      img: '/assets/home/univers/mode-soins/accessoires.png',
      tag: 'accessoires',
    },
    {
      label: t('univers.mode-soins.items.small-appliances', { ns: 'home' }),
      img: '/assets/home/univers/mode-soins/petits-appareils.png',
      tag: 'petits-appareils',
    },
  ],
});

const getTestimonials = (t: TFunction): TestimonialsProps['testimonials'] => [
  {
    title: t('testimonials.1.title', { ns: 'home' }),
    content: t('testimonials.1.content', { ns: 'home' }),
    source: t('testimonials.1.source', { ns: 'home' }),
    img: '/assets/home/testimonials/1.png',
  },
  {
    title: t('testimonials.2.title', { ns: 'home' }),
    content: t('testimonials.2.content', { ns: 'home' }),
    source: t('testimonials.2.source', { ns: 'home' }),
    img: '/assets/home/testimonials/2.png',
  },
  {
    title: t('testimonials.3.title', { ns: 'home' }),
    content: t('testimonials.3.content', { ns: 'home' }),
    source: t('testimonials.3.source', { ns: 'home' }),
    img: '/assets/home/testimonials/3.png',
  },
];

const Home: PageWithLayoutsAndProviders = () => {
  const { t } = useTranslationContext(['home']);
  const { onOpen } = useCatalogueSearchContext();
  const { data: current } = Store.current.useCurrent();
  const userCityOrSub = current?.getCityOrSubResult();

  const { data: nearDonations } = Store.catalogue.useCatalogue({ loc: userCityOrSub?.alias, size: 20, distance: 20, page: 1 });
  const { data: home } = Store.home.useHome();
  const { data: furnitures } = Store.catalogue.useCatalogue({ size: 20, cat: 'meubles', sort: QuerySortEnum.DATE }, !current);
  const { data: clothings } = Store.catalogue.useCatalogue({ size: 20, cat: 'vetements-chaussures-et-accessoires', sort: QuerySortEnum.DATE }, !current);
  const categories = getCategories(t);

  const univers: UniverProps[] = [getJeMinstalle(t), getSport(t), getBricoJardin(t), getFashionAndWellBeing(t)];
  const childcareUnivers = getChildcare(t);

  const testimonials = getTestimonials(t);

  return (
    <>
      <AutoPromo />
      <div className="mb-[42px] space-y-[42px] text-content-primary lg:mb-28 lg:space-y-28">
        <SchemaOrg schema={home?.getBreadcrumbListSchema(t)} />
        <HomeHero
          overtitle={
            <div className="text-title w-full text-center font-semibold text-content-primary md:min-h-8 md:p-1 lg:min-h-14 lg:p-3">
              {!!home?.getUserAccountsNumber() && (
                <>
                  <span className="hidden md:block lg:hidden">{t('hero.overtitle-short', { ns: 'home', nbUserCreated: home.getUserAccountsNumber() })}</span>
                  <span className="max-lg:hidden">{t('hero.overtitle', { ns: 'home', nbUserCreated: home.getUserAccountsNumber() })}</span>
                </>
              )}
            </div>
          }
          title={t('hero.title', { ns: 'home' })}
          content={t('hero.content', { ns: 'home' })}
          button={{ text: t('hero.search-button', { ns: 'home' }), onClick: onOpen }}
        />
        <div className="!mt-8 lg:!mt-14">
          <CategoriesList title={t('categories-list.title', { ns: 'home' })} categories={categories} />
        </div>

        <Univers {...childcareUnivers} />

        <Testimonials testimonials={testimonials} />
        {univers.map(universProps => (
          <Univers key={universProps.title} {...universProps} />
        ))}

        {!!nearDonations?.donations.length && userCityOrSub?.alias && (
          <ContainerWithMaxWidth>
            <CarouselDonationCards
              title={t('carousels.titles.nearUser', { ns: 'home', city: userCityOrSub.name })}
              donations={nearDonations.donations}
              href={nearDonations.getRoute()}
              titleClassName="text-heading font-semibold"
            />
          </ContainerWithMaxWidth>
        )}
        {!!furnitures?.donations.length && !current && (
          <ContainerWithMaxWidth>
            <CarouselDonationCards title={t('carousels.titles.furniture', { ns: 'home' })} donations={furnitures.donations} href={furnitures.getRoute()} titleClassName="text-heading font-semibold" />
          </ContainerWithMaxWidth>
        )}
        {!!clothings?.donations.length && !current && (
          <ContainerWithMaxWidth>
            <CarouselDonationCards title={t('carousels.titles.clothing', { ns: 'home' })} donations={clothings.donations} href={clothings.getRoute()} titleClassName="text-heading font-semibold" />
          </ContainerWithMaxWidth>
        )}
      </div>
    </>
  );
};

export default Home;
